import type { AlertBody, AlertForm } from '@@/shared/alert/types';
import type { PropertySearchCriteria } from '@@/shared/property/search-criteria';

export function useCreateAlert() {
  const { locale } = useI18n();

  return useMutation({
    mutationFn: (form: AlertForm) => {
      const body: AlertBody = {
        ...form,
        locale: locale.value,
      };
      return $fetch(`/api/alerts`, {
        body,
        method: 'POST',
      });
    },
  });
}

export function useReadAlert(managementToken?: string | string[]) {
  return useQuery({
    queryKey: [`ALERT_READ`, managementToken],
    queryFn: () => {
      return $fetch(`/api/alerts/${managementToken}`, {
        method: 'GET',
      });
    },
    retry: 0,
  });
}

export function useDeleteAlert(managementToken?: string | string[]) {
  const { locale } = useI18n();

  return useQuery({
    queryKey: [`ALERT_DELETE`, managementToken],
    queryFn: () => {
      return $fetch(`/api/alerts/${managementToken}`, {
        method: 'DELETE',
        query: { locale: locale.value },
      });
    },
    retry: 0,
  });
}

interface CriteriaParams {
  propertySearchCriteria: PropertySearchCriteria
  labels: Array<string>
}

export function useAlertFormattedSearchCriteria(propertySearchCriteria: Ref<PropertySearchCriteria>) {
  const locationSlugs = toRef(() => propertySearchCriteria.value.locations);
  const { data: locationsFromSelectedSlug } = useLocationFromSlugs(locationSlugs);
  const { t, locale } = useI18n();
  const { amount, surface } = useIadFormat();

  const listFormatter = new Intl.ListFormat(locale.value, {
    style: 'long',
    type: 'disjunction',
  });

  function addTransactionType(params: CriteriaParams) {
    const { propertySearchCriteria, labels } = params;
    if (!propertySearchCriteria.transactionType) return params;
    labels.push(t(`property_serp.transaction_type.${propertySearchCriteria.transactionType}`));
    return params;
  }

  function addPropertyTypes(params: CriteriaParams) {
    const { propertySearchCriteria, labels } = params;
    const { propertyTypes } = propertySearchCriteria;
    if (!propertyTypes.length) return params;
    const propertyTypeLabels = propertyTypes.map(propertyType => t(`property.types_and_sub_types.${propertyType}`));
    labels.push(listFormatter.format(propertyTypeLabels));
    return params;
  }

  function addLocations(params: CriteriaParams) {
    const { propertySearchCriteria, labels } = params;
    const { locations } = propertySearchCriteria;
    if (!locations.length) return params;
    const locationLabels = locations.map((locationSlug) => {
      const foundLocation = locationsFromSelectedSlug.value?.items.find(location => location.slug === locationSlug);
      return foundLocation?.displayName;
    }).filter(item => item != null);
    labels.push(listFormatter.format(locationLabels));
    return params;
  }

  function addPrices(params: CriteriaParams) {
    const { propertySearchCriteria, labels } = params;
    const { priceMin, priceMax } = propertySearchCriteria;
    if (!priceMin && !priceMax) return params;
    if (priceMax && priceMin) labels.push(t('property_serp.alert_dialog.range', { min: amount(priceMin), max: amount(priceMax) }));
    else if (priceMin) labels.push(t('property_serp.alert_dialog.min', { value: amount(priceMin) }));
    else if (priceMax) labels.push(t('property_serp.alert_dialog.max', { value: amount(priceMax) }));
    return params;
  }

  function addSurfaces(params: CriteriaParams) {
    const { propertySearchCriteria, labels } = params;
    const { surfaceMin, surfaceMax } = propertySearchCriteria;
    if (!surfaceMin && !surfaceMax) return params;
    if (surfaceMin && surfaceMax) labels.push(t('property_serp.alert_dialog.range', { min: surfaceMin, max: surface(surfaceMax) }));
    else if (surfaceMin) labels.push(t('property_serp.alert_dialog.min', { value: surface(surfaceMin) }));
    else if (surfaceMax) labels.push(t('property_serp.alert_dialog.max', { value: surface(surfaceMax) }));
    return params;
  }

  function addRooms(params: CriteriaParams) {
    const { propertySearchCriteria, labels } = params;
    const { rooms } = propertySearchCriteria;
    if (!rooms.length) return params;
    if (rooms.length === 1) labels.push(t('property.common.rooms_count', { count: rooms[0] }));
    else {
      const sortedRooms = rooms.toSorted((a, b) => a - b).map(String);
      labels.push(`${listFormatter.format(sortedRooms)} ${t('property_serp.alert_dialog.rooms')}`);
    }
    return params;
  }

  function addBedrooms(params: CriteriaParams) {
    const { propertySearchCriteria, labels } = params;
    const { bedrooms } = propertySearchCriteria;
    if (!bedrooms.length) return params;
    if (bedrooms.length === 1) labels.push(t('property.common.bedrooms_count', { count: bedrooms[0] }));
    else {
      const sortedBedrooms = bedrooms.toSorted((a, b) => a - b).map(String);
      labels.push(`${listFormatter.format(sortedBedrooms)} ${t('property_serp.alert_dialog.bedrooms')}`);
    }
    return params;
  }

  function addFeatures(params: CriteriaParams) {
    const { propertySearchCriteria, labels } = params;
    const { features } = propertySearchCriteria;
    if (!features.length) return params;
    features.forEach((feature) => {
      labels.push(t(`property.feature.${feature}`));
    });
    return params;
  }

  function addEpc(params: CriteriaParams) {
    const { propertySearchCriteria, labels } = params;
    const { epc } = propertySearchCriteria;
    if (!epc.length) return params;
    const epcLabel = listFormatter.format(epc.toSorted((a, b) => a.localeCompare(b)));
    labels.push(t('property_serp.filters.alert.energy_performance', { epc: epcLabel }));
    return params;
  }

  function addGroundSurfaces(params: CriteriaParams) {
    const { propertySearchCriteria, labels } = params;
    const { groundSurfaceMin, groundSurfaceMax } = propertySearchCriteria;
    if (!groundSurfaceMin && !groundSurfaceMax) return params;
    if (groundSurfaceMin && groundSurfaceMax) labels.push(t('property_serp.alert_dialog.ground_surface_range', { min: groundSurfaceMin, max: surface(groundSurfaceMax) }));
    else if (groundSurfaceMin) labels.push(t('property_serp.alert_dialog.ground_surface_min', { value: surface(groundSurfaceMin) }));
    else if (groundSurfaceMax) labels.push(t('property_serp.alert_dialog.ground_surface_max', { value: surface(groundSurfaceMax) }));
    return params;
  }

  function addProjectTypes(params: CriteriaParams) {
    const { propertySearchCriteria, labels } = params;
    const { projectTypes } = propertySearchCriteria;
    if (!projectTypes.length) return params;
    const propertyTypeLabels = projectTypes.map(projectType => t(`property_serp.filters.project_type.${projectType}`));
    labels.push(listFormatter.format(propertyTypeLabels));
    return params;
  }

  const buildCriteriaLabelFunctions = [
    addTransactionType,
    addPropertyTypes,
    addLocations,
    addSurfaces,
    addPrices,
    addRooms,
    addBedrooms,
    addFeatures,
    addEpc,
    addGroundSurfaces,
    addProjectTypes,
  ];

  function createCriteriaLabels(params: CriteriaParams) {
    return buildCriteriaLabelFunctions.reduce((acc, fn) => fn(acc), params);
  }

  const formattedSearchCriteria = computed(() => {
    return createCriteriaLabels({
      propertySearchCriteria: propertySearchCriteria.value,
      labels: [],
    }).labels.filter(Boolean).join(' • ');
  });

  return { formattedSearchCriteria };
}
